import type { SupportedLocale } from '@fleetcore/shared'
import { useTranslation } from 'react-i18next'

import { currentLocale } from './index'

/**
 * BCP-47 tags used for Intl. `en-GB` rather than `en-US` on purpose: the fleet is
 * metric and European, so 24-hour clocks and day/month ordering are what an
 * English-speaking operator on this product expects.
 */
export const INTL_LOCALE: Record<SupportedLocale, string> = {
  fr: 'fr-FR',
  en: 'en-GB',
  // Moroccan Arabic: Western digits and day/month order, as on a Moroccan document.
  ar: 'ar-MA',
}

/**
 * Intl constructors are expensive relative to the call sites that need them —
 * a trip table formats a timestamp per row — so every distinct configuration is
 * built once and reused.
 */
const numberFormatters = new Map<string, Intl.NumberFormat>()
const dateFormatters = new Map<string, Intl.DateTimeFormat>()

function numberFormatter(locale: SupportedLocale, options: Intl.NumberFormatOptions) {
  const key = `${locale}|${JSON.stringify(options)}`
  let formatter = numberFormatters.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(INTL_LOCALE[locale], options)
    numberFormatters.set(key, formatter)
  }
  return formatter
}

function dateFormatter(locale: SupportedLocale, options: Intl.DateTimeFormatOptions) {
  const key = `${locale}|${JSON.stringify(options)}`
  let formatter = dateFormatters.get(key)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(INTL_LOCALE[locale], options)
    dateFormatters.set(key, formatter)
  }
  return formatter
}

export function formatNumber(
  locale: SupportedLocale,
  value: number,
  options: Intl.NumberFormatOptions = {},
): string {
  return numberFormatter(locale, options).format(value)
}

export function formatInteger(locale: SupportedLocale, value: number): string {
  return formatNumber(locale, value, { maximumFractionDigits: 0 })
}

export function formatDecimal(locale: SupportedLocale, value: number, digits = 1): string {
  return formatNumber(locale, value, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

export function formatPercent(locale: SupportedLocale, ratio: number, digits = 0): string {
  return formatNumber(locale, ratio, {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

export function formatDate(locale: SupportedLocale, value: Date | number | string): string {
  return dateFormatter(locale, { dateStyle: 'medium' }).format(new Date(value))
}

export function formatDateTime(locale: SupportedLocale, value: Date | number | string): string {
  return dateFormatter(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

export function formatTime(locale: SupportedLocale, value: Date | number | string): string {
  return dateFormatter(locale, { timeStyle: 'short' }).format(new Date(value))
}

/** Engine hours read as `12 h 30`, never as a decimal — operators bill in minutes. */
export function formatDuration(locale: SupportedLocale, seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  if (hours === 0) return `${formatInteger(locale, minutes)} min`
  return `${formatInteger(locale, hours)} h ${String(minutes).padStart(2, '0')}`
}

export interface Formatters {
  locale: SupportedLocale
  number: (value: number, options?: Intl.NumberFormatOptions) => string
  integer: (value: number) => string
  decimal: (value: number, digits?: number) => string
  percent: (ratio: number, digits?: number) => string
  date: (value: Date | number | string) => string
  dateTime: (value: Date | number | string) => string
  time: (value: Date | number | string) => string
  duration: (seconds: number) => string
}

/** Formatters bound to the language currently rendered, re-created on switch. */
export function useFormatters(): Formatters {
  // Subscribing to `i18n.language` is what re-renders consumers on a language
  // switch; reading currentLocale() alone would leave stale numbers on screen.
  const { i18n } = useTranslation()
  void i18n.language

  const locale = currentLocale()
  return {
    locale,
    number: (value, options) => formatNumber(locale, value, options),
    integer: (value) => formatInteger(locale, value),
    decimal: (value, digits) => formatDecimal(locale, value, digits),
    percent: (ratio, digits) => formatPercent(locale, ratio, digits),
    date: (value) => formatDate(locale, value),
    dateTime: (value) => formatDateTime(locale, value),
    time: (value) => formatTime(locale, value),
    duration: (seconds) => formatDuration(locale, seconds),
  }
}
